import type { ExtensionLogger } from '@sap/ux-specification-types';
import { LogSeverity, MANIFESTPATH } from '@sap/ux-specification-types';
import { log } from '../../extensionLogger';
import i18next from 'i18next';
import * as distTagsJSON from './dist_tag.json';
import { version as packageJsonVersion } from '../../../package.json';

/**
 * Checks if the provided minUI5Version is a variable (starts with '$').
 *
 * @param minUI5Version - The minimum UI5 version to check.
 * @returns {boolean} - true if minUI5Version is a variable, false otherwise.
 */
export function isMinUI5VersionVariable(minUI5Version: string): boolean {
    return minUI5Version.startsWith('$');
}

export class DistTagEvaluator {
    static distTagsOfSpec: string[] = [];

    constructor() {
        if (DistTagEvaluator.distTagsOfSpec.length === 0) {
            DistTagEvaluator.distTagsOfSpec = this.getDistTagsOfVersion();
        }
    }

    /**
     * Resets the buffered list of dist tags (used for unit tests).
     */
    static resetDistTagsOfSpec(): void {
        DistTagEvaluator.distTagsOfSpec = [];
    }

    /**
     * Retrieves the actual spec version from package.json.
     *
     * @returns the version, if found in package.json
     */
    public getSpecVersion(): string | undefined {
        return packageJsonVersion;
    }

    /**
     * Determines the relevant list of dist tags for the current spec version.
     * The dist tags are read from the dist_tag.json (copy from https://github.wdf.sap.corp/NPMJS/FIORI_ELEMENT_SPECIFICATION-1.0.0/blob/master/dist_tag.json).
     * The spec vrsion is read from package.json.
     *
     * @returns an array of the dist tags
     */
    public getDistTagsOfVersion(): string[] {
        let result: string[] = [];
        const distTags = distTagsJSON.distTags;

        let specVersion = this.getSpecVersion();
        if (specVersion) {
            if (specVersion.startsWith('9.9.9')) {
                //master ==> take the newest version
                result = distTags[0].distTag;
            } else {
                specVersion = specVersion.substring(0, specVersion.lastIndexOf('.'));
                const tag = distTags.find((tag) => tag.version.startsWith(specVersion));
                if (tag) {
                    result = tag.distTag;
                }
            }
        }
        return result;
    }

    /**
     * Checks if the given minUI5Version and spec version fit together, based on the distTags stord in ./dist_tag.json.
     *
     * @param {string} minUI5Version - minUI5Version from manifest
     * @param {ExtensionLogger} logger - logger as passed from the API
     */
    public doesUi5VersionFitDistTags(minUI5Version: string | string[], logger: ExtensionLogger): void {
        //env variable is used by tests to disable the check, as test projects may not correspond to the given spec version
        if (process.env.SPEC_NO_VERSION_CHECK === 'true') {
            return;
        }
        let allVersions: string[] = [];
        if (typeof minUI5Version === 'string') {
            allVersions.push(minUI5Version);
        } else if (Array.isArray(minUI5Version)) {
            allVersions = minUI5Version;
        }
        let foundInDistTags = false;
        allVersions.forEach((minUI5Version) => {
            if (!this.checkVersionName(minUI5Version, logger)) {
                return;
            }

            const actualVersion =
                minUI5Version === 'latest' ? 'latest' : minUI5Version.substring(0, minUI5Version.lastIndexOf('.'));
            if (
                DistTagEvaluator.distTagsOfSpec.findIndex((distTagVersion) => distTagVersion.endsWith(actualVersion)) >
                -1
            ) {
                foundInDistTags = true;
            }
        });
        if (!foundInDistTags) {
            const message = i18next.t('WRONGMINUI5VERSION', { minUI5Version });
            log(logger, {
                severity: LogSeverity.Warning,
                message,
                location: {
                    path: MANIFESTPATH
                }
            });
        }
    }

    /**
     * Basic check if the minUI5Version is set and not a variable in the manifest.json.
     *
     * @param minUI5Version - The minimum UI5 version to validate.
     * @param logger - Instance of the extension logger
     * @returns {boolean} - true if minUI5Version is set and not a variable
     */
    private checkVersionName(minUI5Version: string, logger?: ExtensionLogger): boolean {
        if (!minUI5Version || isMinUI5VersionVariable(minUI5Version)) {
            const message = i18next.t('UNKNOWNMINUI5VERSION', { minUI5Version });
            log(logger, {
                severity: LogSeverity.Info,
                message,
                location: {
                    path: MANIFESTPATH
                }
            });
            return false;
        } else {
            return true;
        }
    }
}
